import React, { useState, useRef, useEffect, MouseEvent } from "react";
import { NavLink } from "react-router-dom";
import { prefetchRouteHint } from "@/utils/prefetchRoutes";

interface MainLinkProps {
  to?: string;
  label: string;
  icon: React.ReactNode;
  onVisit: () => void;
  showTooltip: boolean;
  toggleSidebar: boolean;
  target?: string;
}

const styles = {
  mainLink:
    "h-10 rounded-lg w-full text-left flex items-center v-nav-hover",
  mainLinkActive:
    "h-10 rounded-lg border-lg w-full text-left flex items-center v-nav-active",
  navLink:
    "h-10 rounded-lg w-full text-left flex items-center v-nav-hover border-transparent",
  tooltip:
    "absolute left-full ml-1 px-3 py-2 rounded v-nav whitespace-nowrap z-[1000]"
};

export default function MainLink({
  to,
  label,
  icon,
  onVisit,
  showTooltip,
  toggleSidebar,
  target
}: MainLinkProps) {
  const [isHovered, setIsHovered] = useState(false);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    setIsHovered(true);
    if (to && !to.startsWith("http")) prefetchRouteHint(to);
  };

  const handleLeave = () => {
    hideTimeout.current = setTimeout(() => setIsHovered(false), 150);
  };

  // Focus-only prefetch hint — deliberately does NOT reuse handleEnter, which also
  // opens the collapsed-sidebar tooltip. There's no matching onBlur here, so wiring
  // handleEnter to onFocus would leave the tooltip stuck open after tabbing past a link.
  const handleFocusPrefetch = () => {
    if (to && !to.startsWith("http")) prefetchRouteHint(to);
  };

  useEffect(() => {
    return () => { if (hideTimeout.current) clearTimeout(hideTimeout.current); };
  }, []);

  const handleClick = (_e: MouseEvent<HTMLAnchorElement>) => {
    onVisit();
  };

  const isExternal = (to || "").startsWith("http");

  return (
    <div onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {isExternal ? (
        <a
          href={to}
          className={styles.navLink}
          onClick={handleClick}
          target={target}
          rel="noreferrer"
        >
          <div className="flex items-center ml-2">
            {icon}
            {showTooltip && <span className="pl-4 text-sm">{label}</span>}
          </div>
        </a>
      ) : (
        <NavLink
          to={to || ""}
          end
          className={({ isActive }) =>
            isActive ? styles.mainLinkActive : styles.navLink
          }
          onClick={handleClick}
          onFocus={handleFocusPrefetch}
          target={target}
        >
          <div className="flex items-center ml-2">
            {icon}
            {showTooltip && <span className="pl-4 text-sm">{label}</span>}
          </div>
        </NavLink>
      )}

      {!showTooltip && isHovered && (
        <div
          className={styles.tooltip}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          {isExternal ? (
            <a href={to} target={target} rel="noreferrer">{label}</a>
          ) : (
            <NavLink to={to || ""} target={target}>{label}</NavLink>
          )}
        </div>
      )}
    </div>
  );
}
